import { useEffect, useMemo, useRef, useState } from 'react';
import type { TeamMember } from '../../types/api';
import { Eyebrow } from '../ui';
import { cn } from '../ui/cn';
import { cleanRole, properName, summary } from '../../utils/text';

const arrow = 'flex h-12 w-12 items-center justify-center rounded-full border border-line bg-white text-charcoal transition-colors duration-300 hover:border-brand-dark hover:bg-brand-dark hover:text-white';

/** Bande de motif hachuré collée à la photo (équivalent de celle de la référence, en tons de marque). */
function Hatch() {
  return (
    <svg aria-hidden className="absolute bottom-0 right-0 h-56 w-10 text-steel-soft" viewBox="0 0 40 224" preserveAspectRatio="none" fill="none" stroke="currentColor" strokeWidth="1.2">
      {Array.from({ length: 14 }, (_, i) => (
        <g key={i} transform={`translate(0 ${i * 16})`}><path d="M0 16 L20 0 L40 16" /><path d="M0 8 L10 0 M30 0 L40 8" /></g>
      ))}
    </svg>
  );
}

/** Le CEO (nom contenant « Baruka ») passe en premier; l'ordre des autres est conservé. */
const withCeoFirst = (members: TeamMember[]): TeamMember[] => {
  const isCeo = (m: TeamMember) => /baruka/i.test(m.name);
  return [...members.filter(isCeo), ...members.filter((m) => !isCeo(m))];
};

/** Portrait d'un membre de l'équipe: flèches et points; le texte monte en fondu et la photo grandit à chaque changement. */
export default function TeamSpotlight({ members, compact = false, intro }: { members: TeamMember[]; compact?: boolean; intro?: { title: string; text: string } }) {
  const list = useMemo(() => withCeoFirst(members.filter((m) => m.image?.url)), [members]);
  const [i, setI] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  if (list.length === 0) return null;
  const idx = i % list.length;
  const m = list[idx];

  // Sortie rapide de l'ancien contenu (0,2 s), puis entrée du nouveau avec ses animations
  const goTo = (n: number) => {
    const target = (n + list.length) % list.length;
    if (leaving || target === idx) return;
    setLeaving(true);
    timer.current = window.setTimeout(() => { setI(target); setLeaving(false); }, 220);
  };

  const role = cleanRole(m.name, m.position);
  return (
    <section className="bg-white py-16 text-black md:py-24">
      <div className="site-container">
        <Eyebrow className={compact ? 'mb-10' : 'mb-4'}>{compact ? "À la une dans l'équipe" : 'Notre équipe'}</Eyebrow>
        {intro && (
          <div className="mb-12 grid gap-6 lg:grid-cols-2 lg:gap-24">
            <h2 className="font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.75rem]">{intro.title}</h2>
            <p className="text-[1.0625rem] leading-relaxed">{intro.text}</p>
          </div>
        )}
        {!compact && !intro && <h2 className="mb-10 max-w-3xl text-inherit font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal md:mb-14 md:text-[3rem]">Les personnes qui font notre force</h2>}

        <div aria-live="polite" className="grid min-h-[34rem] overflow-hidden bg-surface md:grid-cols-[1fr_24rem]">
          <div className="relative flex flex-col justify-between gap-10 px-8 pb-10 pt-12 md:px-16 md:pt-20">
            <div key={m.id} className={cn('max-w-xl', leaving && 'spot-leave')}>
              <div className={leaving ? '' : 'spot-text-0'}>
                <h3 className="text-inherit font-sans text-[2rem] font-bold normal-case leading-tight tracking-normal">{properName(m.name)}</h3>
                <p className="mt-2 text-base font-medium text-black">{role}</p>
              </div>
              {m.bio && <p className={cn('mt-6 text-[1rem] leading-relaxed text-black', !leaving && 'spot-text-1')}>{summary(m.bio, 320, 3)}</p>}
            </div>

            <div className="relative z-10 flex items-center gap-4">
              <button aria-label="Membre précédent" onClick={() => goTo(idx - 1)} className={arrow}>
                <svg viewBox="0 0 24 12" className="h-3 w-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M24 6H1M6 1L1 6l5 5" /></svg>
              </button>
              <button aria-label="Membre suivant" onClick={() => goTo(idx + 1)} className={arrow}>
                <svg viewBox="0 0 24 12" className="h-3 w-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M0 6h23M18 1l5 5-5 5" /></svg>
              </button>
              <div className="ml-3 flex items-center gap-2" role="tablist" aria-label="Choisir un membre">
                {list.map((x, n) => (
                  <button
                    key={x.id}
                    role="tab"
                    aria-selected={n === idx}
                    aria-label={properName(x.name)}
                    onClick={() => goTo(n)}
                    className={cn('h-2 w-2 transition-colors duration-300', n === idx ? 'bg-brand-dark' : 'rounded-full bg-muted hover:bg-ink')}
                  />
                ))}
              </div>
            </div>
            <Hatch />
          </div>

          <div className="relative min-h-[22rem] overflow-hidden">
            <div key={m.id} className={cn('absolute inset-x-0 bottom-0 h-full overflow-hidden', leaving ? 'spot-leave' : 'spot-image')}>
              <img src={m.image!.url} alt={properName(m.name)} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top md:inset-auto md:bottom-0 md:min-h-[34rem]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
